import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { kategoriAdi, useKatalog, urunYolu } from "../data/katalog";

const WHATSAPP_NUMARA = "905377981129";

/**
 * Malzeme hesaplayıcıları.
 *
 * Her hesaplayıcı: girdiler (adrese yazılır, bağlantı paylaşılabilir) → malzeme listesi
 * → WhatsApp teklifi, yazdırma ve katalogdan önerilen ürünler.
 * Varsayılan sarfiyat değerleri yaygın uygulama değerleridir; kullanıcı ürün etiketine göre değiştirebilir.
 */

// ---------------------------------------------------------------------------
// Yardımcılar
// ---------------------------------------------------------------------------

const sayi = (v) => {
  const n = parseFloat(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) && n > 0 ? n : 0;
};
const bicim = (n, lang, basamak = 1) =>
  (Math.round(n * 10 ** basamak) / 10 ** basamak).toLocaleString(lang, { maximumFractionDigits: basamak });
// Kayan nokta hatası (568,0000001 → 569) olmadan yukarı yuvarlar
const yukari = (x) => Math.ceil(x - 1e-9);
const whatsappLinki = (metin) => `https://wa.me/${WHATSAPP_NUMARA}?text=${encodeURIComponent(metin)}`;

/** Girdileri adres çubuğunda tutar: yenilemede kaybolmaz, bağlantı paylaşılabilir. */
function useGirdiler(varsayilan) {
  const [sorgu, setSorgu] = useSearchParams();
  const degerler = useMemo(() => {
    const d = {};
    for (const [k, v] of Object.entries(varsayilan)) d[k] = sorgu.has(k) ? sorgu.get(k) : v;
    return d;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sorgu]);
  // Birden çok alan aynı anda değişince tek güncelleme yapılır (ardışık çağrılar birbirini ezmesin)
  const guncelle = (nesne) =>
    setSorgu(
      (onceki) => {
        const yeni = new URLSearchParams(onceki);
        for (const [k, v] of Object.entries(nesne)) yeni.set(k, String(v));
        return yeni;
      },
      { replace: true }
    );
  const ayarla = (k) => (v) => guncelle({ [k]: v });
  return [degerler, ayarla, guncelle];
}

// ---------------------------------------------------------------------------
// Ortak arayüz parçaları
// ---------------------------------------------------------------------------

const Alan = ({ etiket, ipucu, birim, deger, degistir, adim = "any", min = 0 }) => (
  <label className="block">
    <span className="mb-1.5 block text-[12.5px] font-medium text-body">{etiket}</span>
    <span className="flex items-stretch overflow-hidden rounded-lg border border-line bg-white transition-colors focus-within:border-ink/40 focus-within:ring-2 focus-within:ring-ink/5">
      <input
        type="number"
        inputMode="decimal"
        min={min}
        step={adim}
        value={deger}
        onChange={(e) => degistir(e.target.value)}
        className="w-full min-w-0 px-3.5 py-2.5 text-[15px] tabular-nums text-ink outline-none"
      />
      {birim && (
        <span className="flex items-center whitespace-nowrap border-s border-line bg-canvas px-3 text-[12.5px] text-muted">
          {birim}
        </span>
      )}
    </span>
    {ipucu && <span className="mt-1 block text-[11.5px] leading-snug text-muted">{ipucu}</span>}
  </label>
);

const Secim = ({ etiket, deger, degistir, secenekler, ipucu }) => (
  <label className="block">
    <span className="mb-1.5 block text-[12.5px] font-medium text-body">{etiket}</span>
    <select
      value={deger}
      onChange={(e) => degistir(e.target.value)}
      className="w-full rounded-lg border border-line bg-white px-3 py-2.5 text-[14px] text-ink outline-none focus:border-ink/40"
    >
      {secenekler.map((s) => (
        <option key={s.deger} value={s.deger}>
          {s.etiket}
        </option>
      ))}
    </select>
    {ipucu && <span className="mt-1 block text-[11.5px] leading-snug text-muted">{ipucu}</span>}
  </label>
);

const Anahtar = ({ secenekler, deger, degistir }) => (
  <div className="inline-flex flex-wrap rounded-lg bg-canvas p-1 text-[13px]">
    {secenekler.map((s) => (
      <button
        key={s.deger}
        type="button"
        onClick={() => degistir(s.deger)}
        className={`rounded-md px-4 py-1.5 font-medium transition-colors ${
          deger === s.deger ? "bg-white text-ink shadow-sm" : "text-muted hover:text-ink"
        }`}
      >
        {s.etiket}
      </button>
    ))}
  </div>
);

const Bolum = ({ no, baslik, children }) => (
  <fieldset className="space-y-4">
    <legend className="mb-1 flex items-center gap-2.5 text-[13px] font-semibold text-ink">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-ink text-[11px] font-bold text-white">{no}</span>
      {baslik}
    </legend>
    {children}
  </fieldset>
);

/** Sonuç: ana değer + kalem kalem malzeme listesi + eylemler */
function SonucKutusu({ t, lang, baslik, ana, birim, kalemler = [], ozet = [], mesaj, katalog }) {
  const [kopyalandi, setKopyalandi] = useState(false);
  const kopyala = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setKopyalandi(true);
      setTimeout(() => setKopyalandi(false), 2000);
    } catch {
      /* pano erişimi yok */
    }
  };

  return (
    <aside className="yazdir-sonuc flex flex-col rounded-2xl bg-ink p-6 text-white lg:sticky lg:top-40 lg:self-start sm:p-7">
      <p className="text-[11px] font-semibold uppercase tracking-eyebrow text-white/60">{baslik}</p>
      <p className="mt-3 flex items-baseline gap-2">
        <span className="text-[44px] font-semibold leading-none tracking-tight tabular-nums">{ana}</span>
        <span className="text-[16px] text-white/70">{birim}</span>
      </p>

      {kalemler.length > 0 && (
        <div className="mt-6 overflow-hidden rounded-xl border border-white/10">
          <p className="bg-white/5 px-4 py-2 text-[11px] font-semibold uppercase tracking-eyebrow text-white/60">
            {t("calc.materialList")}
          </p>
          <ul className="divide-y divide-white/10">
            {kalemler.map(([ad, miktar, not]) => (
              <li key={ad} className="flex items-start justify-between gap-3 px-4 py-2.5 text-[13.5px]">
                <span className="text-white/85">
                  {ad}
                  {not && <span className="block text-[11.5px] text-white/45">{not}</span>}
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-white">{miktar}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {ozet.length > 0 && (
        <ul className="mt-4 space-y-1.5 text-[12.5px] text-white/60">
          {ozet.map(([sol, sag]) => (
            <li key={sol} className="flex justify-between gap-3">
              <span>{sol}</span>
              <span className="tabular-nums text-white/80">{sag}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="yazdir-gizle mt-6 grid gap-2">
        <a
          href={whatsappLinki(mesaj)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-md bg-[#25D366] px-4 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-[#1ebe5b]"
        >
          {t("calc.askQuote")}
        </a>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => window.print()}
            className="rounded-md border border-white/20 px-3 py-2.5 text-[13px] font-medium text-white transition-colors hover:bg-white/10"
          >
            {t("calc.print")}
          </button>
          <button
            type="button"
            onClick={kopyala}
            className="rounded-md border border-white/20 px-3 py-2.5 text-[13px] font-medium text-white transition-colors hover:bg-white/10"
          >
            {kopyalandi ? t("product.linkCopied") : t("calc.copyLink")}
          </button>
        </div>
        {katalog && (
          <Link
            to={katalog.yol}
            className="inline-flex items-center justify-center gap-1.5 rounded-md px-4 py-2 text-[13px] font-medium text-white/80 transition-colors hover:text-white"
          >
            {katalog.etiket}
            <span aria-hidden="true" className="arrow-forward" />
          </Link>
        )}
      </div>
      <p className="mt-4 text-[11.5px] leading-snug text-white/45">{t("calc.disclaimer")}</p>
    </aside>
  );
}

/** Hesaplamaya uygun ürünler: verilen kategorilerden görselli küçük kartlar */
function OnerilenUrunler({ t, lang, kategoriler, oncelikli = [] }) {
  const { urunler, adlar, hazir } = useKatalog();
  const liste = useMemo(() => {
    const secilen = [];
    for (const id of oncelikli) {
      const u = urunler.find((x) => x.id === id);
      if (u) secilen.push(u);
    }
    for (const k of kategoriler) {
      for (const u of urunler) {
        if (secilen.length >= 8) break;
        if (u.kategori === k && !secilen.includes(u)) secilen.push(u);
      }
    }
    // Kategoriler arasında dengeli dağılım: her kategoriden sırayla
    return secilen.slice(0, 8);
  }, [urunler, kategoriler, oncelikli]);

  if (!hazir || !liste.length) return null;
  return (
    <section className="yazdir-gizle mt-10">
      <h2 className="mb-4 text-[16px] font-semibold text-ink">{t("calc.suggested")}</h2>
      <ul className="scrollbar-hide -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-4 sm:overflow-visible sm:px-0">
        {liste.map((u) => (
          <li key={u.id} className="w-40 shrink-0 snap-start sm:w-auto">
            <Link
              to={urunYolu(u)}
              className="group flex h-full flex-col overflow-hidden rounded-xl border border-line bg-white transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_28px_-14px_rgba(0,0,0,0.25)]"
            >
              <span className="relative block aspect-square">
                <img src={u.gorsel} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-contain p-3" />
              </span>
              <span className="block border-t border-line/70 px-3 py-2.5">
                <span className="block text-[10.5px] font-semibold uppercase tracking-eyebrow text-muted">{u.marka}</span>
                <span className="mt-0.5 line-clamp-2 block text-[12.5px] font-medium leading-snug text-ink">{u.ad}</span>
                <span className="mt-1 block truncate text-[11px] text-muted">{kategoriAdi(adlar, u.kategori, lang)}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

const Duzen = ({ girdiler, sonuc, oneriler }) => (
  <>
    <div className="grid gap-6 lg:grid-cols-[1.4fr_1fr]">
      <div className="yazdir-girdi space-y-7 rounded-2xl border border-line bg-white p-6 sm:p-7">{girdiler}</div>
      {sonuc}
    </div>
    {oneriler}
  </>
);

// ---------------------------------------------------------------------------
// 1) Boya
// ---------------------------------------------------------------------------

const BOYA_AMBALAJLARI = [15, 7.5, 2.5];
const ambalajBirlesimi = (litre) => {
  let en = null;
  for (let a = 0; a <= yukari(litre / 15) + 1; a++)
    for (let b = 0; b <= 2; b++)
      for (let c = 0; c <= 3; c++) {
        const top = a * 15 + b * 7.5 + c * 2.5;
        if (top < litre) continue;
        const aday = { a, b, c, top, adet: a + b + c };
        if (!en || aday.top < en.top || (aday.top === en.top && aday.adet < en.adet)) en = aday;
      }
  return en;
};

function BoyaHesabi({ t, lang }) {
  const [g, ayarla] = useGirdiler({ yontem: "oda", en: "4", boy: "5", yuk: "2.7", tavan: "0", alan: "50", bosluk: "4", kat: "2", sarfiyat: "10", astar: "1" });

  const duvar = g.yontem === "oda" ? 2 * (sayi(g.en) + sayi(g.boy)) * sayi(g.yuk) : sayi(g.alan);
  const tavanAlani = g.yontem === "oda" && g.tavan === "1" ? sayi(g.en) * sayi(g.boy) : 0;
  const net = Math.max(0, duvar + tavanAlani - sayi(g.bosluk));
  const litre = sayi(g.sarfiyat) ? (net * Math.max(1, sayi(g.kat))) / sayi(g.sarfiyat) : 0;
  const paket = litre > 0 ? ambalajBirlesimi(litre) : null;
  const paketMetni = paket
    ? BOYA_AMBALAJLARI.map((l, i) => [l, [paket.a, paket.b, paket.c][i]])
        .filter(([, n]) => n > 0)
        .map(([l, n]) => `${n} × ${bicim(l, lang)} L`)
        .join(" + ")
    : "—";
  // Astar: tek kat, ~12 m²/L
  const astarLitre = g.astar === "1" ? net / 12 : 0;

  const kalemler = [
    [t("calc.paint.item"), `${bicim(litre, lang)} L`, paketMetni],
    ...(astarLitre ? [[t("calc.paint.primer"), `${bicim(astarLitre, lang)} L`, t("calc.paint.primerNote")]] : []),
  ];
  const mesaj = t("calc.paint.whatsapp", { alan: bicim(net, lang), kat: sayi(g.kat), litre: bicim(litre, lang), paket: paketMetni });

  return (
    <Duzen
      girdiler={
        <>
          <Bolum no="1" baslik={t("calc.stepArea")}>
            <Anahtar
              deger={g.yontem}
              degistir={ayarla("yontem")}
              secenekler={[
                { deger: "oda", etiket: t("calc.paint.mode_oda") },
                { deger: "alan", etiket: t("calc.paint.mode_alan") },
              ]}
            />
            {g.yontem === "oda" ? (
              <>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Alan etiket={t("calc.width")} birim="m" deger={g.en} degistir={ayarla("en")} />
                  <Alan etiket={t("calc.length")} birim="m" deger={g.boy} degistir={ayarla("boy")} />
                  <Alan etiket={t("calc.height")} birim="m" deger={g.yuk} degistir={ayarla("yuk")} />
                </div>
                <label className="flex items-center gap-2.5 text-[13.5px] text-body">
                  <input type="checkbox" checked={g.tavan === "1"} onChange={(e) => ayarla("tavan")(e.target.checked ? "1" : "0")} className="h-4 w-4 accent-accent" />
                  {t("calc.paint.includeCeiling")}
                </label>
              </>
            ) : (
              <Alan etiket={t("calc.paint.area")} birim="m²" deger={g.alan} degistir={ayarla("alan")} />
            )}
            <Alan etiket={t("calc.openings")} ipucu={t("calc.openingsHint")} birim="m²" deger={g.bosluk} degistir={ayarla("bosluk")} />
          </Bolum>
          <Bolum no="2" baslik={t("calc.stepProduct")}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Alan etiket={t("calc.paint.coats")} birim={t("calc.paint.coatUnit")} deger={g.kat} degistir={ayarla("kat")} adim="1" min={1} />
              <Alan etiket={t("calc.paint.coverage")} ipucu={t("calc.paint.coverageHint")} birim="m²/L" deger={g.sarfiyat} degistir={ayarla("sarfiyat")} />
            </div>
            <label className="flex items-center gap-2.5 text-[13.5px] text-body">
              <input type="checkbox" checked={g.astar === "1"} onChange={(e) => ayarla("astar")(e.target.checked ? "1" : "0")} className="h-4 w-4 accent-accent" />
              {t("calc.paint.addPrimer")}
            </label>
          </Bolum>
        </>
      }
      sonuc={
        <SonucKutusu
          t={t}
          lang={lang}
          baslik={t("calc.paint.result")}
          ana={bicim(litre, lang)}
          birim={t("calc.liter")}
          kalemler={kalemler}
          ozet={[[t("calc.netArea"), `${bicim(net, lang)} m²`]]}
          mesaj={mesaj}
          katalog={{ yol: "/urunler/insaat-boyalari", etiket: t("calc.paint.browse") }}
        />
      }
      oneriler={<OnerilenUrunler t={t} lang={lang} kategoriler={["ic-cephe-boyalari", "astar-ve-macunlar", "boya-rulolari"]} />}
    />
  );
}

// ---------------------------------------------------------------------------
// 2) Tuğla / gazbeton
// ---------------------------------------------------------------------------

const OLCU = /(\d+(?:[.,]\d+)?)\s*×\s*(\d+(?:[.,]\d+)?)(?:\s*×\s*(\d+(?:[.,]\d+)?))?\s*(mm|cm)/;

function DuvarHesabi({ t, lang }) {
  const { urunler } = useKatalog();
  const [g, ayarla, guncelle] = useGirdiler({ uz: "10", yuk: "2.8", bosluk: "3", blok: "ozel", bu: "60", by: "25", derz: "3", fire: "5" });

  const bloklar = useMemo(() => {
    const liste = [];
    for (const u of urunler) {
      if (!["tuglalar", "gazbeton"].includes(u.kategori)) continue;
      const o = (u.ozellikler || []).map((x) => x.match(OLCU)).find(Boolean);
      if (!o) continue;
      const cm = (v) => sayi(v) / (o[4] === "mm" ? 10 : 1);
      const [u1, y1] = o[3] ? [cm(o[1]), cm(o[3])] : [cm(o[1]), cm(o[2])];
      liste.push({ id: u.id, ad: u.ad, urun: u, uzunluk: u1, yukseklik: y1, gazbeton: u.kategori === "gazbeton" });
    }
    return liste;
  }, [urunler]);

  const blok = bloklar.find((b) => b.id === g.blok);
  const bU = blok ? blok.uzunluk : sayi(g.bu);
  const bY = blok ? blok.yukseklik : sayi(g.by);
  const derzCm = sayi(g.derz) / 10;
  const net = Math.max(0, sayi(g.uz) * sayi(g.yuk) - sayi(g.bosluk));
  const m2Basina = bU && bY ? 10000 / ((bU + derzCm) * (bY + derzCm)) : 0;
  const adet = yukari(net * m2Basina * (1 + sayi(g.fire) / 100));
  // Harç: gazbeton ince yapıştırma harcı ~ 2,5 kg/m² (3 mm), tuğla ~ 25 kg/m² (10 mm derz) — yaklaşık
  const gazbetonMu = blok ? blok.gazbeton : sayi(g.derz) <= 5;
  const harcKg = net * (gazbetonMu ? 2.5 : 25);

  const blokAdi = blok ? blok.ad : `${bicim(bU, lang)} × ${bicim(bY, lang)} cm`;
  const mesaj = t("calc.wall.whatsapp", { alan: bicim(net, lang), blok: blokAdi, adet });

  return (
    <Duzen
      girdiler={
        <>
          <Bolum no="1" baslik={t("calc.stepArea")}>
            <div className="grid gap-4 sm:grid-cols-3">
              <Alan etiket={t("calc.wall.length")} birim="m" deger={g.uz} degistir={ayarla("uz")} />
              <Alan etiket={t("calc.height")} birim="m" deger={g.yuk} degistir={ayarla("yuk")} />
              <Alan etiket={t("calc.openings")} ipucu={t("calc.openingsHint")} birim="m²" deger={g.bosluk} degistir={ayarla("bosluk")} />
            </div>
          </Bolum>
          <Bolum no="2" baslik={t("calc.stepProduct")}>
            <Secim
              etiket={t("calc.wall.block")}
              deger={g.blok}
              degistir={(v) => {
                const b = bloklar.find((x) => x.id === v);
                guncelle(b ? { blok: v, derz: b.gazbeton ? "3" : "10" } : { blok: v });
              }}
              secenekler={[
                { deger: "ozel", etiket: t("calc.wall.custom") },
                ...bloklar.map((b) => ({ deger: b.id, etiket: `${b.ad} (${bicim(b.uzunluk, lang)} × ${bicim(b.yukseklik, lang)} cm)` })),
              ]}
            />
            {!blok && (
              <div className="grid gap-4 sm:grid-cols-2">
                <Alan etiket={t("calc.wall.blockLength")} birim="cm" deger={g.bu} degistir={ayarla("bu")} />
                <Alan etiket={t("calc.wall.blockHeight")} birim="cm" deger={g.by} degistir={ayarla("by")} />
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <Alan etiket={t("calc.wall.joint")} ipucu={t("calc.wall.jointHint")} birim="mm" deger={g.derz} degistir={ayarla("derz")} />
              <Alan etiket={t("calc.waste")} ipucu={t("calc.wasteHint")} birim="%" deger={g.fire} degistir={ayarla("fire")} />
            </div>
          </Bolum>
        </>
      }
      sonuc={
        <SonucKutusu
          t={t}
          lang={lang}
          baslik={t("calc.wall.result")}
          ana={adet.toLocaleString(lang)}
          birim={t("calc.pieces")}
          kalemler={[
            [blokAdi, `${adet.toLocaleString(lang)} ${t("calc.pieces")}`, `${bicim(m2Basina, lang)} ${t("calc.pieces")} / m²`],
            [gazbetonMu ? t("calc.wall.thinMortar") : t("calc.wall.mortar"), `${bicim(harcKg, lang, 0)} kg`, t("calc.approx")],
          ]}
          ozet={[[t("calc.netArea"), `${bicim(net, lang)} m²`]]}
          mesaj={mesaj}
          katalog={blok ? { yol: urunYolu(blok.urun), etiket: t("calc.viewProduct") } : { yol: "/urunler/tugla-ve-gazbeton", etiket: t("calc.wall.browse") }}
        />
      }
      oneriler={<OnerilenUrunler t={t} lang={lang} oncelikli={blok ? [blok.id] : []} kategoriler={["gazbeton", "tuglalar", "cimento-esasli-sivalar-ve-orgu-harclari"]} />}
    />
  );
}

// ---------------------------------------------------------------------------
// 3) Alçı / sıva
// ---------------------------------------------------------------------------

const ALCI_TIPLERI = [
  { deger: "makine", sarfiyat: 0.9, kalinlik: 10 },
  { deger: "perlitli", sarfiyat: 0.9, kalinlik: 10 },
  { deger: "saten", sarfiyat: 1.0, kalinlik: 2 },
];

function AlciHesabi({ t, lang }) {
  const [g, ayarla, guncelle] = useGirdiler({ alan: "60", tip: "makine", kal: "10", sarf: "0.9", torba: "35", fire: "5" });
  const kg = sayi(g.alan) * sayi(g.kal) * sayi(g.sarf) * (1 + sayi(g.fire) / 100);
  const torba = sayi(g.torba) ? yukari(kg / sayi(g.torba)) : 0;
  const tipAdi = t(`calc.plaster.type_${g.tip}`);
  const mesaj = t("calc.plaster.whatsapp", { alan: bicim(sayi(g.alan), lang), tip: tipAdi, kalinlik: bicim(sayi(g.kal), lang), torba, kg: sayi(g.torba) });

  return (
    <Duzen
      girdiler={
        <>
          <Bolum no="1" baslik={t("calc.stepArea")}>
            <Alan etiket={t("calc.plaster.area")} birim="m²" deger={g.alan} degistir={ayarla("alan")} />
          </Bolum>
          <Bolum no="2" baslik={t("calc.stepProduct")}>
            <Secim
              etiket={t("calc.plaster.type")}
              deger={g.tip}
              degistir={(v) => {
                const a = ALCI_TIPLERI.find((x) => x.deger === v);
                guncelle({ tip: v, kal: a.kalinlik, sarf: a.sarfiyat });
              }}
              secenekler={ALCI_TIPLERI.map((a) => ({ deger: a.deger, etiket: t(`calc.plaster.type_${a.deger}`) }))}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Alan etiket={t("calc.plaster.thickness")} birim="mm" deger={g.kal} degistir={ayarla("kal")} />
              <Alan etiket={t("calc.plaster.consumption")} ipucu={t("calc.plaster.consumptionHint")} birim="kg/m²/mm" deger={g.sarf} degistir={ayarla("sarf")} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Secim etiket={t("calc.plaster.bag")} deger={g.torba} degistir={ayarla("torba")} secenekler={["25", "30", "35"].map((k) => ({ deger: k, etiket: `${k} kg` }))} />
              <Alan etiket={t("calc.waste")} birim="%" deger={g.fire} degistir={ayarla("fire")} />
            </div>
          </Bolum>
        </>
      }
      sonuc={
        <SonucKutusu
          t={t}
          lang={lang}
          baslik={t("calc.plaster.result")}
          ana={torba.toLocaleString(lang)}
          birim={t("calc.plaster.bags")}
          kalemler={[[tipAdi, `${torba} × ${g.torba} kg`, `${bicim(kg, lang, 0)} kg`]]}
          ozet={[[t("calc.plaster.area"), `${bicim(sayi(g.alan), lang)} m²`]]}
          mesaj={mesaj}
          katalog={{ yol: "/urunler/alci-ve-siva-urunleri", etiket: t("calc.plaster.browse") }}
        />
      }
      oneriler={<OnerilenUrunler t={t} lang={lang} kategoriler={["alci-ve-siva-urunleri", "sivaci-aletleri"]} />}
    />
  );
}

// ---------------------------------------------------------------------------
// 4) Seramik / fayans
// ---------------------------------------------------------------------------

function SeramikHesabi({ t, lang }) {
  const [g, ayarla] = useGirdiler({ alan: "20", fu: "60", fy: "60", kalin: "9", kutu: "1.44", desen: "duz", yap: "5", derz: "3" });
  const fire = g.desen === "capraz" ? 0.15 : 0.1;
  const alanFireli = sayi(g.alan) * (1 + fire);
  const karoM2 = (sayi(g.fu) * sayi(g.fy)) / 10000;
  const adet = karoM2 ? yukari(alanFireli / karoM2) : 0;
  const kutu = sayi(g.kutu) ? yukari(alanFireli / sayi(g.kutu)) : 0;
  const yapistiriciKg = sayi(g.alan) * sayi(g.yap);
  const yapistiriciTorba = yukari(yapistiriciKg / 25);
  // Derz dolgusu (kg/m²) = ((A + B) / (A × B)) × derz genişliği × derz derinliği × 1,6  — A, B, genişlik, derinlik mm
  const A = sayi(g.fu) * 10;
  const B = sayi(g.fy) * 10;
  const derzKgM2 = A && B ? ((A + B) / (A * B)) * sayi(g.derz) * sayi(g.kalin) * 1.6 : 0;
  const derzKg = derzKgM2 * sayi(g.alan);

  const mesaj = t("calc.tile.whatsapp", {
    alan: bicim(sayi(g.alan), lang),
    olcu: `${g.fu}×${g.fy}`,
    adet,
    kutu,
    torba: yapistiriciTorba,
    derz: bicim(derzKg, lang),
  });

  return (
    <Duzen
      girdiler={
        <>
          <Bolum no="1" baslik={t("calc.stepArea")}>
            <Alan etiket={t("calc.tile.area")} birim="m²" deger={g.alan} degistir={ayarla("alan")} />
            <Anahtar
              deger={g.desen}
              degistir={ayarla("desen")}
              secenekler={[
                { deger: "duz", etiket: t("calc.tile.straight") },
                { deger: "capraz", etiket: t("calc.tile.diagonal") },
              ]}
            />
          </Bolum>
          <Bolum no="2" baslik={t("calc.tile.stepTile")}>
            <div className="grid gap-4 sm:grid-cols-3">
              <Alan etiket={t("calc.tile.tileWidth")} birim="cm" deger={g.fu} degistir={ayarla("fu")} />
              <Alan etiket={t("calc.tile.tileHeight")} birim="cm" deger={g.fy} degistir={ayarla("fy")} />
              <Alan etiket={t("calc.tile.thickness")} birim="mm" deger={g.kalin} degistir={ayarla("kalin")} />
            </div>
            <Alan etiket={t("calc.tile.boxM2")} ipucu={t("calc.tile.boxHint")} birim="m²" deger={g.kutu} degistir={ayarla("kutu")} />
          </Bolum>
          <Bolum no="3" baslik={t("calc.tile.stepAdhesive")}>
            <div className="grid gap-4 sm:grid-cols-2">
              <Alan etiket={t("calc.tile.adhesive")} ipucu={t("calc.tile.adhesiveHint")} birim="kg/m²" deger={g.yap} degistir={ayarla("yap")} />
              <Alan etiket={t("calc.tile.joint")} birim="mm" deger={g.derz} degistir={ayarla("derz")} />
            </div>
          </Bolum>
        </>
      }
      sonuc={
        <SonucKutusu
          t={t}
          lang={lang}
          baslik={t("calc.tile.result")}
          ana={kutu.toLocaleString(lang)}
          birim={t("calc.tile.boxes")}
          kalemler={[
            [t("calc.tile.tiles"), `${adet.toLocaleString(lang)} ${t("calc.pieces")}`, `${g.fu} × ${g.fy} cm · %${Math.round(fire * 100)} ${t("calc.waste").toLocaleLowerCase(lang)}`],
            [t("calc.tile.adhesiveItem"), `${yapistiriciTorba} × 25 kg`, `${bicim(yapistiriciKg, lang, 0)} kg`],
            [t("calc.tile.groutItem"), `${bicim(derzKg, lang)} kg`, `${bicim(derzKgM2, lang, 2)} kg/m²`],
          ]}
          ozet={[[t("calc.tile.area"), `${bicim(sayi(g.alan), lang)} m²`]]}
          mesaj={mesaj}
          katalog={{ yol: "/urunler/seramik-mermer-yapistiricilari", etiket: t("calc.tile.browse") }}
        />
      }
      oneriler={<OnerilenUrunler t={t} lang={lang} kategoriler={["seramik-mermer-yapistiricilari", "derz-dolgulari"]} />}
    />
  );
}

// ---------------------------------------------------------------------------
// 5) Beton / şap
// ---------------------------------------------------------------------------

// Yaklaşık çimento dozajları (kg/m³) — şantiyede karılan karışımlar için
const BETON_TIPLERI = [
  { deger: "sap", doz: 350 },
  { deger: "grobeton", doz: 250 },
  { deger: "c25", doz: 350 },
  { deger: "c30", doz: 400 },
];

function BetonHesabi({ t, lang }) {
  const [g, ayarla, guncelle] = useGirdiler({ tip: "sap", en: "5", boy: "4", kal: "5", doz: "350", torba: "50", fire: "5" });
  const hacim = sayi(g.en) * sayi(g.boy) * (sayi(g.kal) / 100) * (1 + sayi(g.fire) / 100);
  const cimentoKg = hacim * sayi(g.doz);
  const torba = sayi(g.torba) ? yukari(cimentoKg / sayi(g.torba)) : 0;
  // Agrega (kum/mıcır) ihtiyacı yaklaşık 1,3 ton/m³ şapta kum, betonda ~1,9 ton/m³ karışık agrega
  const agregaTon = hacim * (g.tip === "sap" ? 1.3 : 1.9);
  const tipAdi = t(`calc.concrete.type_${g.tip}`);
  const mesaj = t("calc.concrete.whatsapp", { tip: tipAdi, hacim: bicim(hacim, lang, 2), torba, kg: g.torba });

  return (
    <Duzen
      girdiler={
        <>
          <Bolum no="1" baslik={t("calc.stepArea")}>
            <div className="grid gap-4 sm:grid-cols-3">
              <Alan etiket={t("calc.width")} birim="m" deger={g.en} degistir={ayarla("en")} />
              <Alan etiket={t("calc.length")} birim="m" deger={g.boy} degistir={ayarla("boy")} />
              <Alan etiket={t("calc.concrete.thickness")} birim="cm" deger={g.kal} degistir={ayarla("kal")} />
            </div>
          </Bolum>
          <Bolum no="2" baslik={t("calc.stepProduct")}>
            <Secim
              etiket={t("calc.concrete.type")}
              deger={g.tip}
              degistir={(v) => {
                guncelle({ tip: v, doz: BETON_TIPLERI.find((x) => x.deger === v).doz });
              }}
              secenekler={BETON_TIPLERI.map((b) => ({ deger: b.deger, etiket: t(`calc.concrete.type_${b.deger}`) }))}
            />
            <div className="grid gap-4 sm:grid-cols-3">
              <Alan etiket={t("calc.concrete.dosage")} ipucu={t("calc.concrete.dosageHint")} birim="kg/m³" deger={g.doz} degistir={ayarla("doz")} />
              <Secim etiket={t("calc.plaster.bag")} deger={g.torba} degistir={ayarla("torba")} secenekler={["25", "50"].map((k) => ({ deger: k, etiket: `${k} kg` }))} />
              <Alan etiket={t("calc.waste")} birim="%" deger={g.fire} degistir={ayarla("fire")} />
            </div>
          </Bolum>
        </>
      }
      sonuc={
        <SonucKutusu
          t={t}
          lang={lang}
          baslik={t("calc.concrete.result")}
          ana={torba.toLocaleString(lang)}
          birim={t("calc.concrete.cementBags")}
          kalemler={[
            [t("calc.concrete.cement"), `${torba} × ${g.torba} kg`, `${bicim(cimentoKg, lang, 0)} kg`],
            [g.tip === "sap" ? t("calc.concrete.sand") : t("calc.concrete.aggregate"), `${bicim(agregaTon, lang)} ton`, t("calc.approx")],
            [t("calc.concrete.readyMix"), `${bicim(hacim, lang, 2)} m³`, t("calc.concrete.readyMixNote")],
          ]}
          ozet={[[t("calc.concrete.volume"), `${bicim(hacim, lang, 2)} m³`]]}
          mesaj={mesaj}
          katalog={{ yol: "/urunler/cimento", etiket: t("calc.concrete.browse") }}
        />
      }
      oneriler={<OnerilenUrunler t={t} lang={lang} kategoriler={["cimento", "zemin-uygulamalari", "grout-beton-ve-harc-katkilari"]} />}
    />
  );
}

// ---------------------------------------------------------------------------
// 6) Alçıpan bölme duvar
// ---------------------------------------------------------------------------

function AlcipanHesabi({ t, lang }) {
  const [g, ayarla] = useGirdiler({ uz: "5", yuk: "2.7", yuz: "2", kat: "1", aralik: "60", bosluk: "2" });
  const net = Math.max(0, sayi(g.uz) * sayi(g.yuk) - sayi(g.bosluk));
  const levhaAlani = net * sayi(g.yuz) * sayi(g.kat);
  const levha = yukari((levhaAlani / 3) * 1.05); // 120 × 250 cm = 3 m² levha, %5 kesim payı
  const dikme = yukari(sayi(g.uz) / (sayi(g.aralik) / 100)) + 1; // C profil dikmeler
  const cProfilM = dikme * sayi(g.yuk);
  const cProfil = yukari(cProfilM / 3); // 3 m boy
  const uProfil = yukari((2 * sayi(g.uz)) / 3); // taban + tavan
  const vida = yukari(levhaAlani * 20); // ~20 vida / m² levha
  const bantM = yukari(net * sayi(g.yuz) * 1.5); // ~1,5 m derz bandı / m² (dış kat)
  const derzAlciKg = net * sayi(g.yuz) * 0.35; // ~0,35 kg / m²

  const mesaj = t("calc.drywall.whatsapp", { alan: bicim(net, lang), levha, c: cProfil, u: uProfil, vida });

  return (
    <Duzen
      girdiler={
        <>
          <Bolum no="1" baslik={t("calc.stepArea")}>
            <div className="grid gap-4 sm:grid-cols-3">
              <Alan etiket={t("calc.wall.length")} birim="m" deger={g.uz} degistir={ayarla("uz")} />
              <Alan etiket={t("calc.height")} birim="m" deger={g.yuk} degistir={ayarla("yuk")} />
              <Alan etiket={t("calc.openings")} birim="m²" deger={g.bosluk} degistir={ayarla("bosluk")} />
            </div>
          </Bolum>
          <Bolum no="2" baslik={t("calc.drywall.stepSystem")}>
            <div className="grid gap-4 sm:grid-cols-3">
              <Secim etiket={t("calc.drywall.faces")} deger={g.yuz} degistir={ayarla("yuz")} secenekler={[{ deger: "1", etiket: t("calc.drywall.oneFace") }, { deger: "2", etiket: t("calc.drywall.twoFaces") }]} />
              <Secim etiket={t("calc.drywall.layers")} deger={g.kat} degistir={ayarla("kat")} secenekler={[{ deger: "1", etiket: t("calc.drywall.single") }, { deger: "2", etiket: t("calc.drywall.double") }]} />
              <Secim etiket={t("calc.drywall.spacing")} deger={g.aralik} degistir={ayarla("aralik")} secenekler={["40", "60"].map((k) => ({ deger: k, etiket: `${k} cm` }))} />
            </div>
          </Bolum>
        </>
      }
      sonuc={
        <SonucKutusu
          t={t}
          lang={lang}
          baslik={t("calc.drywall.result")}
          ana={levha.toLocaleString(lang)}
          birim={t("calc.drywall.boards")}
          kalemler={[
            [t("calc.drywall.boardItem"), `${levha} ${t("calc.pieces")}`, "120 × 250 cm"],
            [t("calc.drywall.cProfile"), `${cProfil} × 3 m`, `${dikme} ${t("calc.drywall.studs")}`],
            [t("calc.drywall.uProfile"), `${uProfil} × 3 m`],
            [t("calc.drywall.screws"), `${vida.toLocaleString(lang)} ${t("calc.pieces")}`, t("calc.approx")],
            [t("calc.drywall.tape"), `${bantM} m`, t("calc.approx")],
            [t("calc.drywall.jointFiller"), `${bicim(derzAlciKg, lang)} kg`, t("calc.approx")],
          ]}
          ozet={[[t("calc.netArea"), `${bicim(net, lang)} m²`]]}
          mesaj={mesaj}
          katalog={{ yol: "/urunler/alcipan-ve-kuru-duvar-sistemleri", etiket: t("calc.drywall.browse") }}
        />
      }
      oneriler={<OnerilenUrunler t={t} lang={lang} kategoriler={["alcipan-ve-kuru-duvar-sistemleri"]} />}
    />
  );
}

// ---------------------------------------------------------------------------
// Sayfa
// ---------------------------------------------------------------------------

const SEKMELER = [
  { anahtar: "boya", bilesen: BoyaHesabi, ikon: "🎨" },
  { anahtar: "duvar", bilesen: DuvarHesabi, ikon: "🧱" },
  { anahtar: "seramik", bilesen: SeramikHesabi, ikon: "🔲" },
  { anahtar: "beton", bilesen: BetonHesabi, ikon: "🏗️" },
  { anahtar: "alci", bilesen: AlciHesabi, ikon: "🪣" },
  { anahtar: "alcipan", bilesen: AlcipanHesabi, ikon: "📐" },
];

export default function Hesaplayicilar() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "tr").split("-")[0];
  const [sorgu, setSorgu] = useSearchParams();
  const aktif = SEKMELER.find((s) => s.anahtar === sorgu.get("tur")) || SEKMELER[0];
  const Bilesen = aktif.bilesen;

  // Hesaplayıcı değişince girdiler sıfırlanır (başka hesaplayıcının parametreleri taşınmasın)
  const sec = (anahtar) => setSorgu({ tur: anahtar }, { replace: false });

  useEffect(() => {
    document.title = `${t(`calc.tabs.${aktif.anahtar}`)} — ${t("calc.eyebrow")} — ${t("siteName")}`;
  }, [aktif, t]);

  return (
    <section className="container-page section">
      <div className="page-head yazdir-gizle">
        <p className="eyebrow">{t("calc.eyebrow")}</p>
        <h1 className="page-title mt-3">{t("calc.title")}</h1>
        <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-body">{t("calc.subtitle")}</p>
      </div>

      {/* Hesaplayıcı seçimi: kartlar */}
      <div role="tablist" className="yazdir-gizle mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {SEKMELER.map((s) => {
          const secili = aktif === s;
          return (
            <button
              key={s.anahtar}
              role="tab"
              aria-selected={secili}
              onClick={() => sec(s.anahtar)}
              className={`flex flex-col items-start gap-1.5 rounded-xl border p-4 text-start transition-all ${
                secili ? "border-ink bg-ink text-white shadow-lg" : "border-line bg-white text-ink hover:-translate-y-0.5 hover:border-ink/30"
              }`}
            >
              <span className="text-[24px]" aria-hidden="true">{s.ikon}</span>
              <span className="text-[14px] font-semibold leading-tight">{t(`calc.tabs.${s.anahtar}`)}</span>
              <span className={`text-[11.5px] leading-snug ${secili ? "text-white/65" : "text-muted"}`}>{t(`calc.tabsHint.${s.anahtar}`)}</span>
            </button>
          );
        })}
      </div>

      {/* Yazdırılan sayfanın başlığı */}
      <div className="yazdir-baslik hidden">
        <p className="text-[20px] font-semibold">Akkuzey Yapı Malzemeleri — {t(`calc.tabs.${aktif.anahtar}`)}</p>
        <p className="text-[12px]">+90 537 798 11 29 · {new Date().toLocaleDateString(lang)}</p>
      </div>

      <Bilesen key={aktif.anahtar} t={t} lang={lang} />
    </section>
  );
}

